import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { fr, nlBE } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import "react-day-picker/dist/style.css";

const toStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fromStr = (s) => (s ? new Date(s + "T00:00:00") : undefined);

const NavIconLeft = () => <ChevronLeft className="h-4 w-4" />;
const NavIconRight = () => <ChevronRight className="h-4 w-4" />;

const triggerBase =
  "flex items-center justify-between gap-3 w-full bg-transparent border-b py-2 text-xs outline-none transition-colors duration-300 focus:border-accent-yellow";

const dayPickerClassNames = {
  months: "flex flex-col",
  month: "space-y-3",
  caption: "flex justify-center pt-1 relative items-center",
  caption_label: "text-sm font-medium text-white capitalize",
  nav: "space-x-1 flex items-center",
  nav_button:
    "h-7 w-7 rounded-md inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/5 transition-colors",
  nav_button_previous: "absolute left-1",
  nav_button_next: "absolute right-1",
  table: "w-full border-collapse",
  head_row: "flex",
  head_cell: "text-white/35 w-9 font-normal text-[0.7rem] uppercase tracking-wide",
  row: "flex w-full mt-1",
  cell: "relative p-0 text-center text-sm",
  day: "h-9 w-9 rounded-md inline-flex items-center justify-center font-normal text-white/80 hover:bg-white/5 hover:text-white transition-colors",
  day_selected: "!bg-accent-yellow !text-ink font-semibold hover:!bg-accent-yellow hover:!text-ink",
  day_today: "text-accent-yellow",
  day_outside: "text-white/20",
  day_disabled: "text-white/15 hover:bg-transparent hover:text-white/15 cursor-not-allowed",
  day_hidden: "invisible",
};

export const DateField = ({ value, onChange, min, placeholder, error, testid, lang }) => {
  const [open, setOpen] = useState(false);
  const selected = fromStr(value);
  const disabledBefore = min ? new Date(min + "T00:00:00") : undefined;
  const formatted = selected
    ? selected.toLocaleDateString(lang === "fr" ? "fr-FR" : "nl-BE", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`${triggerBase} ${error ? "border-accent-yellow" : "border-white/20"}`}
        >
          <span className={selected ? "text-white truncate" : "text-white/40"}>
            {selected ? formatted : placeholder}
          </span>
          <CalendarIcon size={16} className="shrink-0 text-white/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-3 rounded-xl border border-white/10 bg-ink-2 text-white shadow-xl shadow-black/40"
      >
        <DayPicker
          mode="single"
          className="!m-0"
          selected={selected}
          defaultMonth={selected || disabledBefore}
          disabled={disabledBefore ? { before: disabledBefore } : undefined}
          locale={lang === "fr" ? fr : nlBE}
          showOutsideDays
          weekStartsOn={1}
          classNames={dayPickerClassNames}
          components={{
            IconLeft: NavIconLeft,
            IconRight: NavIconRight,
          }}
          onSelect={(d) => {
            if (d) {
              onChange(toStr(d));
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export const SlotField = ({ value, onChange, options, placeholder, error, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`${triggerBase} ${error ? "border-accent-yellow" : "border-white/20"}`}
        >
          <span className={value ? "text-white" : "text-white/40"}>{value || placeholder}</span>
          <ChevronDown
            size={16}
            className={`shrink-0 text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] p-1.5 rounded-xl border border-white/10 bg-ink-2 text-white shadow-xl shadow-black/40 max-h-64 overflow-y-auto"
      >
        <div className="grid grid-cols-2 gap-1">
          {options.map((s) => {
            const active = value === s;
            return (
              <button
                key={s}
                type="button"
                data-testid={`${testid}-option-${s}`}
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                className={`flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                  active
                    ? "bg-accent-yellow text-ink font-semibold"
                    : "text-white/80 hover:bg-white/5 hover:text-white"
                }`}
              >
                {s}
                {active && <Check size={14} />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
